// src/App.js
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Suspense, lazy, useEffect, useMemo } from "react";

import BookingConfirm from "./pages/BookingConfirm";
import BookingSuccess from "./pages/BookingSuccess";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import MarketplaceForgotPassword from "./pages/MarketplaceForgotPassword";
import MarketplaceVerifyEmail from "./pages/MarketplaceVerifyEmail";
import VerifyEmail from "./pages/VerifyEmail";
import Register from "./pages/Register";
import MarketplaceResetPassword from "./pages/MarketplaceResetPassword";
import ResetPassword from "./pages/ResetPassword";
import StaffInviteAccept from "./pages/StaffInviteAccept";
import MyBookings from "./pages/MyBookings";
import SelectSalon from "./pages/SelectSalon";
import JoinSalon from "./pages/JoinSalon";

import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import MarketplaceHeader from "./components/marketplace/MarketplaceHeader";
import SuperAdminReportsRouteShell from "./components/reports/SuperAdminReportsRouteShell";
import ManageStaff from "./pages/ManageStaff";
import ManageServices from "./pages/ManageServices";
import ClientPortalSettings from "./pages/ClientPortalSettings";
import ClientPortalAIAssistant from "./pages/ClientPortalAIAssistant";
import AppointmentEmailSettings from "./pages/AppointmentEmailSettings";
import OnlineCancellationsSettings from "./pages/OnlineCancellationsSettings";
import Reports from "./pages/Reports";
import SalesTicketsTrendsReport from "./pages/SalesTicketsTrendsReport";
import TicketSummaryReport from "./pages/TicketSummaryReport";
import StaffPerformanceReport from "./pages/StaffPerformanceReport";
import LiveDashboardReport from "./pages/LiveDashboardReport";
import GiftCardsCashDrawerReport from "./pages/GiftCardsCashDrawerReport";
import TotalSheetReport from "./pages/TotalSheetReport";
import TaxLiabilityReport from "./pages/TaxLiabilityReport";
import DailyClosingReport from "./pages/DailyClosingReport";
import CashBalanceReport from "./pages/CashBalanceReport";
import SalesByEmployeeReport from "./pages/SalesByEmployeeReport";
import WalkInQueueReport from "./pages/WalkInQueueReport";
import ProfitabilityEstimateReport from "./pages/ProfitabilityEstimateReport";
import SalonValuationReport from "./pages/SalonValuationReport";
import BreakEvenSensitivityReport from "./pages/BreakEvenSensitivityReport";
import AppointmentStatusReport from "./pages/AppointmentStatusReport";
import CancellationAnalyticsReport from "./pages/CancellationAnalyticsReport";
import MaintenanceCleaningAnalyticsReport from "./pages/MaintenanceCleaningAnalyticsReport";
import ServiceAnalyticsReport from "./pages/ServiceAnalyticsReport";
import CategoryAnalyticsReport from "./pages/CategoryAnalyticsReport";
import GiftCardAnalyticsReport from "./pages/GiftCardAnalyticsReport";
import TrainingProgressReport from "./pages/TrainingProgressReport";
import ClientAnalyticsReport from "./pages/reports/ClientAnalyticsReport";
import RewardsReport from "./pages/reports/RewardsReport";
import ClientSatisfactionDashboard from "./pages/reports/ClientSatisfactionDashboard";
import MarketingCampaignsReport from "./pages/reports/MarketingCampaignsReport";
import ReviewFollowups from "./pages/manager/ReviewFollowups";
import ManageUsersRoles from "./pages/ManageUsersRoles";
import ManageGiftCards from "./pages/ManageGiftCards";
import MaintenanceCleaning from "./pages/MaintenanceCleaning";
import DailyTasks from "./pages/DailyTasks";
import Inventory from "./pages/Inventory";
import ClientImportHistory from "./pages/ClientImportHistory";
import ServiceImportHistory from "./pages/ServiceImportHistory";
import StaffImportHistory from "./pages/StaffImportHistory";
import StaffEdit from "./pages/StaffEdit";
import ClientsDuplicateEmails from "./pages/ClientsDuplicateEmails";
import AdminUsers from "./pages/AdminUsers";
import BusinessSettings from "./pages/BusinessSettings";
import AdminBusinessSettings from "./pages/AdminBusinessSettings";
import TechnicianSetup from "./pages/TechnicianSetup";
import Profile from "./pages/Profile";
import TechnicianHours from "./pages/TechnicianHours";
import TicketList from "./pages/TicketList";
import TicketDetail from "./pages/TicketDetail";
import TicketCollectPayment from "./pages/TicketCollectPayment";
import TicketsNewRoute from "./pages/TicketsNewRoute";
import HouseSaleRoute from "./pages/HouseSaleRoute";
import TicketImportHistory from "./pages/TicketImportHistory";
import SuperAdminConsole from "./pages/SuperAdminConsole";
import SuperAdminReports from "./pages/SuperAdminReports";
import SuperAdminReportsHome from "./pages/SuperAdminReportsHome";
import SuperAdminRegistrationActivity from "./pages/SuperAdminRegistrationActivity";
import SuperAdminRegistrationAnalytics from "./pages/SuperAdminRegistrationAnalytics";
import SuperAdminLoginActivity from "./pages/SuperAdminLoginActivity";
import SuperAdminLoginAnalytics from "./pages/SuperAdminLoginAnalytics";
import SuperAdminClientAssignment from "./pages/SuperAdminClientAssignment";
import SuperAdminBillingRiskAlerts from "./pages/SuperAdminBillingRiskAlerts";
import SuperAdminBillingCoupons from "./pages/SuperAdminBillingCoupons";
import SuperAdminBillingCouponReport from "./pages/SuperAdminBillingCouponReport";
import SuperAdminSalesByEmployee from "./pages/SuperAdminSalesByEmployee";
import SuperAdminAppointmentsStatus from "./pages/SuperAdminAppointmentsStatus";
import SuperAdminCancellationsNoShows from "./pages/SuperAdminCancellationsNoShows";
import SuperAdminCategoryAnalytics from "./pages/SuperAdminCategoryAnalytics";
import SuperAdminServiceAnalytics from "./pages/SuperAdminServiceAnalytics";
import SuperAdminClientAnalytics from "./pages/SuperAdminClientAnalytics";
import SuperAdminClientSatisfaction from "./pages/SuperAdminClientSatisfaction";
import SuperAdminComplaintThemes from "./pages/SuperAdminComplaintThemes";
import SuperAdminTaxLiabilitySummary from "./pages/SuperAdminTaxLiabilitySummary";
import SuperAdminTotalSheet from "./pages/SuperAdminTotalSheet";
import SuperAdminStaffPerformance from "./pages/SuperAdminStaffPerformance";
import SuperAdminLiveDashboard from "./pages/SuperAdminLiveDashboard";
import SuperAdminExtendedReport from "./pages/SuperAdminExtendedReport";
import SuperAdminGiftCardAnalytics from "./pages/SuperAdminGiftCardAnalytics";
import SuperAdminRewardsAnalytics from "./pages/SuperAdminRewardsAnalytics";
import SuperAdminSalonValuationReport from "./pages/SuperAdminSalonValuationReport";
import SuperAdminBreakEvenSensitivityReport from "./pages/SuperAdminBreakEvenSensitivityReport";
import SuperAdminBugReports from "./pages/SuperAdminBugReports";
import SuperAdminMaintenanceCleaningAnalyticsReport from "./pages/SuperAdminMaintenanceCleaningAnalyticsReport";
import CashDrawer from "./pages/CashDrawer";
import TimeClock from "./pages/TimeClock";
import BillingHome from "./pages/BillingHome";
import BillingCardOnFile from "./pages/BillingCardOnFile";
import BillingReplaceCard from "./pages/BillingReplaceCard";
import BillingStatements from "./pages/BillingStatements";
import BugReport from "./pages/BugReport";
import GoogleDriveIntegration from "./pages/GoogleDriveIntegration";
import RewardsProgram from "./pages/RewardsProgram";
import ClientReviews from "./pages/marketing/ClientReviews";
import ClientSurveys from "./pages/marketing/ClientSurveys";
import ClientSurveyDetail from "./pages/marketing/ClientSurveyDetail";
import FeedbackFollowups from "./pages/marketing/FeedbackFollowups";
import FeedbackFollowupDetail from "./pages/marketing/FeedbackFollowupDetail";
import ResponseTemplates from "./pages/marketing/ResponseTemplates";
import Campaigns from "./pages/marketing/Campaigns";
import CampaignTemplates from "./pages/marketing/CampaignTemplates";
import CampaignDetail from "./pages/marketing/CampaignDetail";
import EmailOptIn from "./pages/marketing/EmailOptIn";
import EmailOptOuts from "./pages/marketing/EmailOptOuts";
import EmailReminders from "./pages/marketing/EmailReminders";
import Payroll from "./pages/Payroll";
import HRPayStubs from "./pages/HRPayStubs";
import TrainingDevelopment from "./pages/TrainingDevelopment";
import SalonSpeakEnglish from "./pages/SalonSpeakEnglish";
import SalonSpeakReception from "./pages/SalonSpeakReception";
import SalonSpeakUpsell from "./pages/SalonSpeakUpsell";
import RequireSalonContext from "./components/RequireSalonContext";
import { useBrand, BrandProvider } from "./context/BrandContext";
import { buildHostUrl, getCrossDomainRedirectUrl } from "./config/hostRouting";
import { getMarketplaceAuthenticatedLandingPath } from "./utils/marketplaceShellRouting";
import { getTechnicianLandingPath } from "./utils/technicianRouting";
import { getOpsAuthenticatedRootPath } from "./utils/rootRouting";
import useMyPermissions from "./hooks/useMyPermissions";
import RequireRentalsFeature from "./components/rentals/RequireRentalsFeature";
import { RentalsFeatureProvider } from "./context/RentalsFeatureContext";
import {
  getMarketplaceOperatorAgreementsPath,
  getMarketplaceOperatorBookingsPath,
  getMarketplaceOperatorHubPath,
  getMarketplaceOperatorLeasesPath,
  getMarketplaceOperatorPaymentsPath,
  getMarketplaceOperatorSettingsPath,
  getMarketplaceOperatorWorkspaceCreatePath,
  getMarketplaceOperatorWorkspaceDetailPath,
  getMarketplaceOperatorWorkspaceEditPath,
  getMarketplaceOperatorWorkspacesPath,
} from "./utils/marketplaceOperatorRoutes";

import EditSalon from "./pages/EditSalon";
import SalonDetail from "./pages/SalonDetail";
import FindSalons from "./pages/FindSalons";

// User management
import UserList from "./pages/UserList";
import CreateUser from "./pages/CreateUser";
import EditUser from "./pages/EditUser";
import ChangePassword from "./pages/ChangePassword";

// New Clients page
import Clients from "./pages/Clients";

import { UiModalProvider } from "./context/UiModalContext";
import MarketplaceAccountBookingsPage from "./pages/marketplace/MarketplaceAccountBookingsPage";
import MarketplaceAccountPaymentsPage from "./pages/marketplace/MarketplaceAccountPaymentsPage";
import MarketplaceAccessNeededPage from "./pages/marketplace/MarketplaceAccessNeededPage";
import MarketplaceEllyannaAccessPage from "./pages/marketplace/MarketplaceEllyannaAccessPage";
import MarketplaceOperatorHubPage from "./pages/marketplace/MarketplaceOperatorHubPage";
import MarketplaceAccountProfilePage from "./pages/marketplace/MarketplaceAccountProfilePage";
import MarketplaceCheckoutPage from "./pages/marketplace/MarketplaceCheckoutPage";
import EllyannaBookingHome from "./pages/EllyannaBookingHome";
import SalonBusinessCpaLanding from "./pages/SalonBusinessCpaLanding";
import SalonBusinessCpaDashboard from "./pages/salonBusinessCpa/SalonBusinessCpaDashboard";
import SalonBusinessCpaDocuments from "./pages/salonBusinessCpa/SalonBusinessCpaDocuments";
import SalonBusinessCpaChecklist from "./pages/salonBusinessCpa/SalonBusinessCpaChecklist";
import SalonBusinessCpaGstHst from "./pages/salonBusinessCpa/SalonBusinessCpaGstHst";
import SalonBusinessCpaPayroll from "./pages/salonBusinessCpa/SalonBusinessCpaPayroll";
import SalonBusinessCpaBookkeeping from "./pages/salonBusinessCpa/SalonBusinessCpaBookkeeping";
import SalonBusinessCpaCorporateTax from "./pages/salonBusinessCpa/SalonBusinessCpaCorporateTax";
import SalonBusinessCpaPersonalTax from "./pages/salonBusinessCpa/SalonBusinessCpaPersonalTax";
import SalonBusinessCpaFinancialStatements from "./pages/salonBusinessCpa/SalonBusinessCpaFinancialStatements";
import SalonBusinessCpaProfileIntake from "./pages/salonBusinessCpa/SalonBusinessCpaProfileIntake";
import RequireAccountingAccess from "./pages/salonBusinessCpa/RequireAccountingAccess";
import MarketplaceLanding from "./pages/marketplace/MarketplaceLanding";
import MarketplaceRentalSummaryPage from "./pages/marketplace/MarketplaceRentalSummaryPage";
import MarketplaceRentalSummaryRedirectPage from "./pages/marketplace/MarketplaceRentalSummaryRedirectPage";
import MarketplaceSearchPage from "./pages/marketplace/MarketplaceSearchPage";
import MarketplaceSuperAdminPage from "./pages/marketplace/MarketplaceSuperAdminPage";
import MarketplaceWorkspaceDetailPage from "./pages/marketplace/MarketplaceWorkspaceDetailPage";
import AdminMarketplaceBillingPage from "./pages/myboothshare/AdminMarketplaceBillingPage";
import CheckoutFailurePage from "./pages/myboothshare/CheckoutFailurePage";
import CheckoutSuccessPage from "./pages/myboothshare/CheckoutSuccessPage";
import DisputeSubmissionPage from "./pages/myboothshare/DisputeSubmissionPage";
import OperatorLedgerPage from "./pages/myboothshare/OperatorLedgerPage";
import OperatorPayoutsPage from "./pages/myboothshare/OperatorPayoutsPage";
import OperatorWorkspaceAgreementsPage from "./pages/myboothshare/OperatorWorkspaceAgreementsPage";
import OperatorWorkspaceCompliancePage from "./pages/myboothshare/OperatorWorkspaceCompliancePage";
import OperatorWorkspaceOwnershipPage from "./pages/myboothshare/OperatorWorkspaceOwnershipPage";
import OperatorWorkspaceSupportPage from "./pages/myboothshare/OperatorWorkspaceSupportPage";
import OperatorWorkspaceWarningsPage from "./pages/myboothshare/OperatorWorkspaceWarningsPage";
import SetupWizard from "./pages/SetupWizard";
import ManagerJoinRequests from "./pages/ManagerJoinRequests";
import RentalAgreementsPage from "./pages/manager/rentals/RentalAgreementsPage";
import WorkspaceEditorPage from "./pages/manager/rentals/WorkspaceEditorPage";
import WorkspaceInventoryPage from "./pages/manager/rentals/WorkspaceInventoryPage";
import RentalSettingsPage from "./pages/manager/rentals/RentalSettingsPage";
import ClientPortalLayout from "./pages/clientPortal/ClientPortalLayout";
import PortalLanding from "./pages/clientPortal/PortalLanding";
import PortalRegister from "./pages/clientPortal/PortalRegister";
import PortalLogin from "./pages/clientPortal/PortalLogin";
import PortalProfile from "./pages/clientPortal/PortalProfile";
import PortalBook from "./pages/clientPortal/PortalBook";
import PortalReschedule from "./pages/clientPortal/PortalReschedule";
import PortalManageAppointment from "./pages/clientPortal/PortalManageAppointment";
import PortalMyAppointments from "./pages/clientPortal/PortalMyAppointments";
import PortalSetupCredentials from "./pages/clientPortal/PortalSetupCredentials";
import PortalRewards from "./pages/clientPortal/PortalRewards";
import PortalGiftCards from "./pages/clientPortal/PortalGiftCards";
import PortalForgotPassword from "./pages/clientPortal/PortalForgotPassword";
import PortalResetPassword from "./pages/clientPortal/PortalResetPassword";
import PortalReview from "./pages/clientPortal/PortalReview";
import PortalReviewPreview from "./pages/clientPortal/PortalReviewPreview";
import PublicFeedbackRedirect from "./pages/PublicFeedbackRedirect";
import PublicReviewRedirect from "./pages/PublicReviewRedirect";
import PublicSurvey from "./pages/PublicSurvey";
import ComplaintThemesReport from "./pages/reports/ComplaintThemesReport";
import EmailSubscriptionReport from "./pages/reports/EmailSubscriptionReport";
import EmailSubscribe from "./pages/EmailSubscribe";
import EmailUnsubscribe from "./pages/EmailUnsubscribe";
import EmailSubscriptionFeedback from "./pages/EmailSubscriptionFeedback";
import "./pages/MarketingSectionCompact.css";
import "./pages/ManageSectionCompact.css";

const ManagerSchedule = lazy(() => import("./pages/ManagerSchedule"));
const TechnicianSchedule = lazy(() => import("./pages/TechnicianSchedule"));

const SALON_SPEAK_PERMISSION_ROLES = new Set([
  "technician",
  "apprentice",
  "solo_technician",
]);

const isMarketplaceShellPath = (pathname = "", isMarketplaceHost = false) => {
  if (!pathname) {
    return false;
  }
  if (pathname === "/" && isMarketplaceHost) {
    return true;
  }
  return (
    pathname === "/search" ||
    pathname.startsWith("/account/") ||
    pathname.startsWith("/workspaces/") ||
    pathname.startsWith("/checkout/")
  );
};

function RouteLoader() {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-slate-500">
      Loading...
    </div>
  );
}

  function MySalonsRedirect() {
    const { role: currentRole, user } = useAuth();
    const runtimeBrand = useBrand();
    if (runtimeBrand.isMarketplaceHost) {
      return (
        <Navigate
          to={getMarketplaceAuthenticatedLandingPath({
            role: currentRole,
            user,
          })}
          replace
        />
      );
    }
    const normalizedRole = (currentRole || "").toLowerCase();
    const targetPath =
      normalizedRole === "technician" ? "/technician/schedule" : "/manager/schedule";
    return <Navigate to={targetPath} replace />;
  }

  function LegacyCreateSalonRedirect() {
    const { user } = useAuth();
    const isSuperAdmin = Boolean(user?.is_super_admin);
    const targetPath = isSuperAdmin ? "/admin/business-settings" : "/";
    return <Navigate to={targetPath} replace />;
  }

  function LegacyBoothCreateRedirect() {
    return <Navigate to={getMarketplaceOperatorWorkspaceCreatePath()} replace />;
  }

  function LegacyBoothListRedirect() {
    return <Navigate to={getMarketplaceOperatorWorkspacesPath()} replace />;
  }

  function LegacyBoothEditRedirect() {
    const { id } = useParams();
    return <Navigate to={getMarketplaceOperatorWorkspaceEditPath(id)} replace />;
  }

  function LegacyBoothCheckoutRedirect() {
    const { id } = useParams();
    return <Navigate to={`/checkout/${id}`} replace />;
  }

  function LegacyBookingDisputeRedirect() {
    const { bookingId } = useParams();
    return <Navigate to={`/account/rentals/${bookingId}/dispute`} replace />;
  }

export function AppContent() {
  const managerRoles = [
    "admin",
    "host",
    "owner",
    "manager",
    "receptionist",
    "solo_technician",
    "reception",
  ];
  const independentProfessionalRoles = ["independent_professional"];
  const billingRoles = ["admin", "owner", ...independentProfessionalRoles];
  const bookingMarketingRoles = [
    "admin",
    "host",
    "owner",
    "manager",
    ...independentProfessionalRoles,
  ];
  const campaignMarketingRoles = ["admin", "owner", "manager", ...independentProfessionalRoles];
  const operatorScheduleRoles = [
    ...managerRoles,
    "technician",
    "apprentice",
    ...independentProfessionalRoles,
  ];
  const soloOperatorManageRoles = [...managerRoles, ...independentProfessionalRoles];
  const inventoryRoles = [
    "admin",
    "host",
    "owner",
    "manager",
    "receptionist",
    "reception",
    ...independentProfessionalRoles,
  ];
  const soloOperatorReportRoles = [...managerRoles, ...independentProfessionalRoles];
  const trainingProgressRoles = ["admin", "host", "owner", "manager"];
  const { token, user, technicianStatus, role, loading } = useAuth();
  const currentRoleValue = role || user?.role || "";
  const normalizedRole = (currentRoleValue || "").toLowerCase();
  const location = useLocation();
  const runtimeBrand = useBrand();
  const hostRedirectUrl = useMemo(
    () =>
      getCrossDomainRedirectUrl({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        hostname: runtimeBrand.hostname,
        isMarketplaceHost: runtimeBrand.isMarketplaceHost,
        isAccountingHost: runtimeBrand.isAccountingHost,
      }),
    [
      location.hash,
      location.pathname,
      location.search,
      runtimeBrand.hostname,
      runtimeBrand.isAccountingHost,
      runtimeBrand.isMarketplaceHost,
    ]
  );
  const isClientPortalRoute = location.pathname.startsWith("/portal/");
  const isMarketplaceShellRoute = isMarketplaceShellPath(
    location.pathname,
    runtimeBrand.isMarketplaceHost
  );
  const requiresSalonSpeakPermission =
    SALON_SPEAK_PERMISSION_ROLES.has(normalizedRole);
  const { permissions: routePermissions, loading: loadingRoutePermissions } =
    useMyPermissions({
      enabled: Boolean(token && user?.salon_id && requiresSalonSpeakPermission),
    });
  const canAccessSalonSpeakModules =
    !requiresSalonSpeakPermission
      ? true
      : loadingRoutePermissions || routePermissions === null
      ? null
      : Boolean(routePermissions?.["training.view"]);

  const HomeRedirect = () => {
    if (runtimeBrand.isMarketplaceHost) {
      if (loading) {
        return null;
      }
      if (!token) {
        return <MarketplaceLanding />;
      }
      return (
        <Navigate
          to={getMarketplaceAuthenticatedLandingPath({
            role: normalizedRole,
            user,
          })}
          replace
        />
      );
    }
    if (runtimeBrand.isAccountingHost) {
      return <SalonBusinessCpaLanding />;
    }
    if (loading) {
      return null;
    }
    if (!token) {
      return <EllyannaBookingHome />;
    }

    return (
      <Navigate
        to={getOpsAuthenticatedRootPath({
          normalizedRole,
          user,
          technicianStatus,
        })}
        replace
      />
    );
  };

  const TechnicianProfileRedirect = () => {
    const hasSelectedSalon =
      technicianStatus?.has_selected_salon ?? Boolean(user?.salon_id);
    const staffSetupComplete = technicianStatus?.staff_setup_complete ?? false;
    const profileComplete = technicianStatus?.profile_complete ?? false;
    const destination = getTechnicianLandingPath({
      hasSelectedSalon,
      staffSetupComplete,
      profileComplete,
    });
    return <Navigate to={destination} replace />;
  };

  const ComingSoon = ({ title }) => (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">{title}</h1>
      <p className="text-slate-600">This area is coming soon.</p>
    </div>
  );

  const AppShell = ({ children }) => {
    const isScheduleRoute =
      location.pathname.startsWith("/manager/schedule") ||
      location.pathname.startsWith("/technician/schedule");
    const shouldUsePublicProductChrome =
      runtimeBrand.isMarketplaceHost || runtimeBrand.isAccountingHost;
    const mainClassName = runtimeBrand.isMarketplaceHost
      ? "myboothshare-theme flex-1 min-h-0 overflow-y-auto"
      : runtimeBrand.isAccountingHost
      ? "flex-1 min-h-0 overflow-y-auto"
      : `flex-1 min-h-0 pt-[calc(56px+env(safe-area-inset-top,0px))] md:pt-0 ${
          isScheduleRoute ? "overflow-hidden" : "overflow-y-auto"
        }`;

    return (
      <div className="min-h-screen h-[100dvh] flex flex-col overflow-hidden">
        {shouldUsePublicProductChrome ? <MarketplaceHeader /> : <Navbar />}
        <main className={mainClassName}>
          {children}
        </main>
      </div>
    );
  };

  const SalonSpeakRouteGuard = ({ children }) => {
    if (canAccessSalonSpeakModules === null) {
      return (
        <div className="max-w-4xl mx-auto p-6 text-sm text-slate-600">
          Loading permissions...
        </div>
      );
    }
    if (!canAccessSalonSpeakModules) {
      return <Navigate to="/manager/hr/training" replace />;
    }
    return children;
  };

  const withSuperAdminReportsShell = (element) => (
    <SuperAdminReportsRouteShell>{element}</SuperAdminReportsRouteShell>
  );

  const renderRentalsRoute = (element) => (
    <ProtectedRoute
      allowedRoles={managerRoles}
      allowedPermissions={[
        "rentals.view",
        "rentals.manage",
        "rentals.marketplace_manage",
      ]}
    >
      <RequireSalonContext>
        <RequireRentalsFeature>{element}</RequireRentalsFeature>
      </RequireSalonContext>
    </ProtectedRoute>
  );

  const renderMarketplaceOperatorRoute = (element) => (
    <ProtectedRoute
      allowedRoles={managerRoles}
      allowedPermissions={[
        "rentals.view",
        "rentals.manage",
        "rentals.marketplace_manage",
      ]}
    >
      <RequireSalonContext>
        <RequireRentalsFeature>{element}</RequireRentalsFeature>
      </RequireSalonContext>
    </ProtectedRoute>
  );

  const MarketplaceHostRedirect = ({ to }) => {
    const href = buildHostUrl(runtimeBrand.marketplaceHostName, to);

    useEffect(() => {
      if (typeof window === "undefined") {
        return;
      }
      window.location.replace(href);
    }, [href]);

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
        <div className="max-w-md rounded-3xl border border-slate-200 bg-white px-8 py-10 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
            Moving To MyBoothShare
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-slate-900">
            Rental setup now lives on MyBoothShare
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Workspace setup, rental approvals, agreements, and rental settings now open from the
            MyBoothShare operator shell instead of the Ellyanna dashboard.
          </p>
          <a
            href={href}
            className="mt-6 inline-flex rounded-full bg-[#2f3566] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#252b57]"
          >
            Continue
          </a>
        </div>
      </div>
    );
  };

  const MarketplaceWorkspaceDetailRedirect = () => {
    const { id } = useParams();
    return <MarketplaceHostRedirect to={getMarketplaceOperatorWorkspaceDetailPath(id)} />;
  };

  const MarketplaceWorkspaceEditRedirect = () => {
    const { id } = useParams();
    return <MarketplaceHostRedirect to={getMarketplaceOperatorWorkspaceEditPath(id)} />;
  };

  const MarketplaceBookingsRedirect = () => {
    const location = useLocation();
    return (
      <MarketplaceHostRedirect to={getMarketplaceOperatorBookingsPath(location.search)} />
    );
  };

  const MarketplaceLeasesRedirect = () => {
    const location = useLocation();
    return (
      <MarketplaceHostRedirect to={getMarketplaceOperatorLeasesPath(location.search)} />
    );
  };

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const titleBase = runtimeBrand.documentTitle;
    if (isMarketplaceShellRoute && location.pathname !== "/") {
      document.title = `${titleBase} | Workspace Marketplace`;
      return;
    }
    document.title = titleBase;
  }, [isMarketplaceShellRoute, location.pathname, runtimeBrand.documentTitle]);

  useEffect(() => {
    if (!hostRedirectUrl || typeof window === "undefined") {
      return;
    }
    window.location.replace(hostRedirectUrl);
  }, [hostRedirectUrl]);

  if (hostRedirectUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
        <div className="max-w-md rounded-3xl border border-slate-200 bg-white px-8 py-10 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
            Switching Product Context
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-slate-900">
            Redirecting to the right workspace
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Management and salon operations stay on Ellyanna. Marketplace browsing
            and renter flows stay on Ellyanna MyBoothShare.
          </p>
          <a
            href={hostRedirectUrl}
            className="mt-6 inline-flex rounded-full bg-[#2f3566] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#252b57]"
          >
            Continue
          </a>
        </div>
      </div>
    );
  }

  if (isClientPortalRoute) {
    return (
      <Routes>
        <Route
          path="/portal/appointments/manage/:token"
          element={<PortalManageAppointment />}
        />
        <Route path="/portal/setup" element={<PortalSetupCredentials />} />
        <Route path="/portal/reset-password" element={<PortalResetPassword />} />
        <Route path="/portal/review" element={<PortalReview />} />
        <Route path="/portal/review-preview" element={<PortalReviewPreview />} />
          <Route path="/portal/salon/:salonId" element={<ClientPortalLayout />}>
            <Route index element={<PortalLanding />} />
            <Route path="book" element={<PortalBook />} />
            <Route path="appointments" element={<PortalMyAppointments />} />
            <Route
              path="appointments/:appointmentId/reschedule"
              element={<PortalReschedule />}
            />
            <Route path="gift-cards" element={<PortalGiftCards />} />
            <Route path="rewards" element={<PortalRewards />} />
            <Route path="login" element={<PortalLogin />} />
          <Route path="register" element={<PortalRegister />} />
          <Route path="forgot-password" element={<PortalForgotPassword />} />
          <Route path="profile" element={<PortalProfile />} />
        </Route>
      </Routes>
    );
  }

  if (runtimeBrand.isAccountingHost) {
    return (
      <Routes>
        <Route path="/" element={<SalonBusinessCpaLanding />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/salonbusinesscpa"
          element={
            <ProtectedRoute>
              <SalonBusinessCpaDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/profile"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaProfileIntake />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/documents"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaDocuments />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/checklist"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaChecklist />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/gst-hst"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaGstHst />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/payroll"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaPayroll />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/bookkeeping"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaBookkeeping />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/corporate-tax"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaCorporateTax />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/personal-tax"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaPersonalTax />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salonbusinesscpa/financial-statements"
          element={
            <ProtectedRoute>
              <RequireAccountingAccess>
                <SalonBusinessCpaFinancialStatements />
              </RequireAccountingAccess>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<SalonBusinessCpaLanding />} />
      </Routes>
    );
  }

  return (
    <>
      <AppShell>
        <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/" element={<HomeRedirect />} />
        <Route
          path="/select-salon"
          element={
            <ProtectedRoute>
              <SelectSalon />
            </ProtectedRoute>
          }
        />
        <Route
          path="/join-salon"
          element={
            <ProtectedRoute>
              <JoinSalon />
            </ProtectedRoute>
          }
        />
        <Route path="/find-salons" element={<FindSalons />} />
        <Route path="/search" element={<MarketplaceSearchPage />} />
        <Route path="/access-needed" element={<MarketplaceAccessNeededPage />} />
        <Route path="/workspaces/:id" element={<MarketplaceWorkspaceDetailPage />} />
        <Route path="/checkout/:id" element={<MarketplaceCheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout/failure" element={<CheckoutFailurePage />} />
        <Route
          path="/account/operator"
          element={
            <ProtectedRoute>
              <MarketplaceOperatorHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/bookings"
          element={<Navigate to="/account/rentals" replace />}
        />
        <Route
          path="/account/rentals"
          element={
            <ProtectedRoute>
              <MarketplaceAccountBookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/payments"
          element={
            <ProtectedRoute>
              <MarketplaceAccountPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/rentals/:rentalId"
          element={
            <ProtectedRoute>
              <MarketplaceRentalSummaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/rentals/by-booking/:bookingId"
          element={
            <ProtectedRoute>
              <MarketplaceRentalSummaryRedirectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/bookings/:bookingId/dispute"
          element={<LegacyBookingDisputeRedirect />}
        />
        <Route
          path="/account/rentals/:bookingId/dispute"
          element={
            <ProtectedRoute>
              <DisputeSubmissionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <MarketplaceAccountProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/ellyanna-access"
          element={
            <ProtectedRoute>
              <MarketplaceEllyannaAccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/host-start"
          element={<Navigate to={getMarketplaceOperatorWorkspaceCreatePath()} replace />}
        />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route
          path="/marketplace/forgot-password"
          element={<MarketplaceForgotPassword />}
        />
        <Route
          path="/marketplace/verify-email"
          element={<MarketplaceVerifyEmail />}
        />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/marketplace/reset-password"
          element={<MarketplaceResetPassword />}
        />
        <Route path="/staff-invite/:token" element={<StaffInviteAccept />} />
        <Route path="/salons/:id" element={<SalonDetail />} />
        <Route path="/f/:token" element={<PublicFeedbackRedirect />} />
        <Route path="/s/:token" element={<PublicSurvey />} />
        <Route path="/email/subscribe" element={<EmailSubscribe />} />
        <Route path="/email/unsubscribe/preview" element={<EmailUnsubscribe />} />
        <Route path="/email/unsubscribe" element={<EmailUnsubscribe />} />
        <Route path="/unsubscribe/preview" element={<EmailUnsubscribe />} />
        <Route path="/unsubscribe/:prefToken" element={<EmailUnsubscribe />} />
        <Route
          path="/email/subscription-feedback"
          element={<EmailSubscriptionFeedback />}
        />
        <Route
          path="/r/public-review/:promptId"
          element={<PublicReviewRedirect />}
        />
        <Route
          path="/r/public-review/survey/:promptId"
          element={<PublicReviewRedirect />}
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-salons"
          element={
            <ProtectedRoute>
              <MySalonsRedirect />
            </ProtectedRoute>
          }
        />

        {/* Redirect old URL /manage-salons -> /my-salons */}
        <Route
          path="/manage-salons"
          element={<Navigate to="/my-salons" replace />}
        />

          <Route
            path="/salons/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "host", "owner", "manager", "solo_technician"]}
              >
                <EditSalon />
              </ProtectedRoute>
            }
          />

          <Route
            path="/create-salon"
            element={<LegacyCreateSalonRedirect />}
          />

        <Route
          path="/host/*"
          element={<Navigate to={getMarketplaceOperatorWorkspaceCreatePath()} replace />}
        />

        {/* TECH BOOKINGS / GENERAL PROTECTED ROUTES */}
        <Route
          path="/booths/:id/checkout"
          element={<LegacyBoothCheckoutRedirect />}
        />

        <Route
          path="/booking/confirm"
          element={
            <ProtectedRoute>
              <BookingConfirm />
            </ProtectedRoute>
          }
        />

        <Route
          path="/success"
          element={
            <ProtectedRoute>
              <BookingSuccess />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          }
        />

        {/* TECHNICIAN ONBOARDING */}
        <Route
          path="/technician/profile"
          element={
            <ProtectedRoute allowedRoles={["technician"]}>
              <TechnicianProfileRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/technician/setup"
          element={
            <ProtectedRoute allowedRoles={["technician"]}>
              <RequireSalonContext>
                <TechnicianSetup />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/technician/schedule"
          element={
            <ProtectedRoute allowedRoles={["technician"]}>
              <RequireSalonContext>
                <Suspense fallback={<RouteLoader />}>
                  <TechnicianSchedule />
                </Suspense>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/technician/services"
          element={
            <ProtectedRoute
              allowedRoles={["technician", "owner", "manager", "admin"]}
            >
              <ManageServices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/technician/hours"
          element={
            <ProtectedRoute
              allowedRoles={["technician", "owner", "manager", "admin"]}
            >
              <TechnicianHours />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "host",
                "owner",
                "manager",
                "technician",
                "solo_technician",
                "reception",
                "independent_professional",
              ]}
            >
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* HOST ONLY: CREATE BOOTH */}
        <Route
          path="/create-booth"
          element={renderRentalsRoute(<LegacyBoothCreateRedirect />)}
        />

        {/* HOST OR ADMIN: MY BOOTHS */}
        <Route
          path="/my-booths"
          element={renderRentalsRoute(<LegacyBoothListRedirect />)}
        />

        {/* HOST OR ADMIN: EDIT BOOTH */}
        <Route
          path="/edit-booth/:id"
          element={renderRentalsRoute(<LegacyBoothEditRedirect />)}
        />

        {/* CLIENT MANAGEMENT */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          }
        />

        {/* TICKETS */}
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <TicketList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/imports"
          element={
            <ProtectedRoute>
              <TicketImportHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/:ticketId"
          element={
            <ProtectedRoute>
              <TicketDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/:ticketId/collect-payment"
          element={
            <ProtectedRoute>
              <TicketCollectPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/new"
          element={
            <ProtectedRoute>
              <TicketsNewRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/house-sale"
          element={
            <ProtectedRoute>
              <HouseSaleRoute />
            </ProtectedRoute>
          }
        />

        {/* USER MANAGEMENT (still guarded by backend as admin-only) */}
        <Route
          path="/users"
          element={
            <ProtectedRoute requireSuperAdmin>
              <UserList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/create"
          element={
            <ProtectedRoute requireSuperAdmin>
              <CreateUser />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/edit/:id"
          element={
            <ProtectedRoute requireSuperAdmin>
              <EditUser />
            </ProtectedRoute>
          }
        />

        {/* MANAGER EXPERIENCE */}
        <Route
          path="/manager/schedule"
          element={
            <ProtectedRoute
              allowedRoles={operatorScheduleRoles}
              requiredPermissions={["schedule.view_all", "schedule.edit"]}
            >
              <Suspense fallback={<RouteLoader />}>
                <ManagerSchedule mode="manager" />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/tickets"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["tickets.manage"]}
            >
              <ComingSoon title="Tickets" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage"
          element={<Navigate to="/manager/manage/staff" replace />}
        />
        <Route
          path="/manager/manage/staff"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["staff.manage"]}
            >
              <ManageStaff />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/users"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["users.manage_roles"]}
            >
              <ManageUsersRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/:id"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["staff.manage"]}
            >
              <StaffEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/services"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorManageRoles}
              allowedPermissions={["services.manage"]}
            >
              <ManageServices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/maintenance-cleaning"
          element={
            <ProtectedRoute
              allowedRoles={[
                ...managerRoles,
                ...independentProfessionalRoles,
                "technician",
                "apprentice",
                "receptionist",
              ]}
            >
              <RequireSalonContext>
                <MaintenanceCleaning />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/daily-tasks"
          element={
            <ProtectedRoute
              allowedRoles={[
                ...managerRoles,
                ...independentProfessionalRoles,
                "technician",
                "apprentice",
                "receptionist",
              ]}
            >
              <RequireSalonContext>
                <DailyTasks />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/inventory"
          element={
            <ProtectedRoute allowedRoles={inventoryRoles}>
              <RequireSalonContext>
                <Inventory />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/client-portal"
          element={
            <ProtectedRoute
              allowedRoles={bookingMarketingRoles}
              allowedPermissions={["settings.manage"]}
            >
              <ClientPortalSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/client-portal/ai-assistant"
          element={
            <ProtectedRoute
              allowedRoles={bookingMarketingRoles}
              allowedPermissions={["settings.manage"]}
            >
              <ClientPortalAIAssistant />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/online-booking/appointment-emails"
          element={
            <ProtectedRoute
              allowedRoles={bookingMarketingRoles}
              allowedPermissions={["settings.manage"]}
            >
              <RequireSalonContext>
                <AppointmentEmailSettings />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/online-booking/cancellations"
          element={
            <ProtectedRoute
              allowedRoles={bookingMarketingRoles}
              allowedPermissions={["settings.manage"]}
            >
              <RequireSalonContext>
                <OnlineCancellationsSettings />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/gift-cards"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorManageRoles}
              allowedPermissions={["settings.manage"]}
            >
              <ManageGiftCards />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/client-imports"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorManageRoles}
              allowedPermissions={["clients.manage"]}
            >
              <ClientImportHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/staff-imports"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["staff.manage"]}
            >
              <StaffImportHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/service-imports"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorManageRoles}
              allowedPermissions={["services.manage"]}
            >
              <ServiceImportHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/clients/duplicate-emails"
          element={
            <ProtectedRoute
              allowedRoles={bookingMarketingRoles}
              allowedPermissions={["clients.manage", "clients.view_contact"]}
            >
              <RequireSalonContext>
                <ClientsDuplicateEmails />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/join-requests"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["users.manage_roles"]}
            >
              <ManagerJoinRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/cash-drawer"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorManageRoles}
              allowedPermissions={["tickets.manage"]}
            >
              <RequireSalonContext>
                <CashDrawer />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/time-clock"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["timeclock.view", "timeclock.edit", "timeclock.delete"]}
            >
              <RequireSalonContext>
                <TimeClock />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/billing"
          element={
            <ProtectedRoute allowedRoles={billingRoles}>
              <BillingHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/billing/card"
          element={
            <ProtectedRoute allowedRoles={billingRoles}>
              <BillingCardOnFile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/billing/replace-card"
          element={
            <ProtectedRoute allowedRoles={billingRoles}>
              <BillingReplaceCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/manage/billing/statements"
          element={
            <ProtectedRoute allowedRoles={billingRoles}>
              <BillingStatements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bug-report"
          element={
            <ProtectedRoute>
              <BugReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/integrations/google-drive"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <GoogleDriveIntegration />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/rewards"
          element={
            <ProtectedRoute
              allowedRoles={campaignMarketingRoles}
              allowedPermissions={["rewards.view"]}
            >
              <RequireSalonContext>
                <RewardsProgram />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/campaigns"
          element={
            <ProtectedRoute
              allowedRoles={campaignMarketingRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <Campaigns />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/campaigns/templates"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <CampaignTemplates />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/campaigns/:id"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <CampaignDetail />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/email-opt-in"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <EmailOptIn />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/email-opt-outs"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "owner", "manager"]}
              allowedPermissions={["emails.manage_opt_outs"]}
            >
              <RequireSalonContext>
                <EmailOptOuts />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/email-reminders"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <EmailReminders />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/client-reviews"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <ClientReviews />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/client-surveys"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <ClientSurveys />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/survey-builder"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <Navigate to="/manager/marketing/client-reviews" replace />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/client-surveys/:requestId"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <ClientSurveyDetail />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/feedback-followups"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <FeedbackFollowups />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/feedback-followups/:followupId"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <FeedbackFollowupDetail />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/response-templates"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <ResponseTemplates />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/marketing/feedback-moderation"
          element={
            <ProtectedRoute allowedRoles={bookingMarketingRoles}>
              <RequireSalonContext>
                <Navigate to="/manager/marketing/client-surveys" replace />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/payroll"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "owner", "manager"]}
              allowedPermissions={["payroll.manage"]}
            >
              <RequireSalonContext>
                <Payroll />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/pay-stubs"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "owner", "manager"]}
              allowedPermissions={["payroll.manage"]}
            >
              <RequireSalonContext>
                <HRPayStubs />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/training"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <TrainingDevelopment />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/training/salonspeak/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakEnglish />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/training/salonspeak-reception/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakReception />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/training/salonspeak-upsell/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakUpsell />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/salonspeak/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakEnglish />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/salonspeak-reception/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakReception />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/hr/salonspeak-upsell/*"
          element={
            <ProtectedRoute>
              <RequireSalonContext>
                <SalonSpeakRouteGuard>
                  <SalonSpeakUpsell />
                </SalonSpeakRouteGuard>
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/marketing-campaigns"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <MarketingCampaignsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/email-subscription"
          element={
            <ProtectedRoute allowedRoles={campaignMarketingRoles}>
              <RequireSalonContext>
                <EmailSubscriptionReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/live-dashboard"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <LiveDashboardReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/total-sheet"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <TotalSheetReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/tax-liability"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <TaxLiabilityReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/daily-closing"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <DailyClosingReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/profitability-estimate"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ProfitabilityEstimateReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/salon-valuation"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <SalonValuationReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/break-even-sensitivity"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <BreakEvenSensitivityReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/cash-balance"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <CashBalanceReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/sales-by-employee"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <SalesByEmployeeReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/walk-in-queue"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <WalkInQueueReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/appointments-status"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <AppointmentStatusReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/cancellations"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <CancellationAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/maintenance-cleaning"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <MaintenanceCleaningAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/sales-tickets"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <SalesTicketsTrendsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/ticket-summary"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <TicketSummaryReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/staff-performance"
          element={
            <ProtectedRoute
              allowedRoles={managerRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <StaffPerformanceReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/training-progress"
          element={
            <ProtectedRoute
              allowedRoles={trainingProgressRoles}
              allowedPermissions={["reports.view", "training.manage"]}
            >
              <RequireSalonContext>
                <TrainingProgressReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/gift-cards-cash-drawer"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <GiftCardsCashDrawerReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/service-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ServiceAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/category-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <CategoryAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/gift-card-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <GiftCardAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/rewards"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <RewardsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/client-satisfaction"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ClientSatisfactionDashboard />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/complaint-themes"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ComplaintThemesReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/review-followups"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "host", "owner", "manager"]}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ReviewFollowups />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/service-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ServiceAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/category-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <CategoryAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/gift-card-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <GiftCardAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/rewards"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <RewardsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/reports/client-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ClientAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/rentals"
          element={renderRentalsRoute(<MarketplaceHostRedirect to={getMarketplaceOperatorHubPath()} />)}
        />
        <Route
          path="/manager/rentals/workspaces"
          element={renderRentalsRoute(<MarketplaceHostRedirect to={getMarketplaceOperatorWorkspacesPath()} />)}
        />
        <Route
          path="/manager/rentals/workspaces/new"
          element={renderRentalsRoute(<MarketplaceHostRedirect to={getMarketplaceOperatorWorkspaceCreatePath()} />)}
        />
        <Route
          path="/manager/rentals/workspaces/:id"
          element={
            renderRentalsRoute(
              <MarketplaceWorkspaceDetailRedirect />
            )
          }
        />
        <Route
          path="/manager/rentals/workspaces/:id/edit"
          element={
            renderRentalsRoute(
              <MarketplaceWorkspaceEditRedirect />
            )
          }
        />
        <Route
          path="/manager/rentals/settings"
          element={renderRentalsRoute(<MarketplaceHostRedirect to={getMarketplaceOperatorSettingsPath()} />)}
        />
        <Route
          path="/manager/rentals/agreements"
          element={renderRentalsRoute(<MarketplaceHostRedirect to={getMarketplaceOperatorAgreementsPath()} />)}
        />
        <Route
          path="/manager/rentals/bookings"
          element={renderRentalsRoute(<MarketplaceBookingsRedirect />)}
        />
        <Route
          path="/manager/rentals/leases"
          element={renderRentalsRoute(<MarketplaceLeasesRedirect />)}
        />
        <Route
          path="/account/operator/workspaces"
          element={renderMarketplaceOperatorRoute(<WorkspaceInventoryPage />)}
        />
        <Route
          path="/account/operator/workspaces/new"
          element={renderMarketplaceOperatorRoute(<WorkspaceEditorPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id"
          element={renderMarketplaceOperatorRoute(<WorkspaceEditorPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/edit"
          element={renderMarketplaceOperatorRoute(<WorkspaceEditorPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/compliance"
          element={renderMarketplaceOperatorRoute(<OperatorWorkspaceCompliancePage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/ownership"
          element={renderMarketplaceOperatorRoute(<OperatorWorkspaceOwnershipPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/agreements"
          element={renderMarketplaceOperatorRoute(<OperatorWorkspaceAgreementsPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/warnings"
          element={renderMarketplaceOperatorRoute(<OperatorWorkspaceWarningsPage />)}
        />
        <Route
          path="/account/operator/workspaces/:id/support"
          element={renderMarketplaceOperatorRoute(<OperatorWorkspaceSupportPage />)}
        />
        <Route
          path="/account/operator/settings"
          element={renderMarketplaceOperatorRoute(<RentalSettingsPage />)}
        />
        <Route
          path="/account/operator/agreements"
          element={renderMarketplaceOperatorRoute(<RentalAgreementsPage />)}
        />
        <Route
          path="/account/operator/bookings"
          element={<Navigate to={getMarketplaceOperatorAgreementsPath()} replace />}
        />
        <Route
          path="/account/operator/leases"
          element={<Navigate to={getMarketplaceOperatorAgreementsPath()} replace />}
        />
        <Route
          path="/account/operator/payouts"
          element={renderMarketplaceOperatorRoute(<OperatorPayoutsPage />)}
        />
        <Route
          path="/account/operator/ledger"
          element={renderMarketplaceOperatorRoute(<OperatorLedgerPage />)}
        />
        <Route
          path="/account/operator/finance"
          element={<Navigate to={getMarketplaceOperatorPaymentsPath()} replace />}
        />
        <Route
          path="/account/operator/payments"
          element={renderMarketplaceOperatorRoute(<AdminMarketplaceBillingPage />)}
        />
        <Route
          path="/reports/client-analytics"
          element={
            <ProtectedRoute
              allowedRoles={soloOperatorReportRoles}
              allowedPermissions={["reports.view"]}
            >
              <RequireSalonContext>
                <ClientAnalyticsReport />
              </RequireSalonContext>
            </ProtectedRoute>
          }
        />

        {/* BUSINESS SETUP */}
        <Route
          path="/setup"
          element={
            <ProtectedRoute allowedPermissions={["settings.manage"]}>
              <SetupWizard />
            </ProtectedRoute>
          }
        />

        {/* ADMIN */}
        <Route
          path="/admin/business"
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <BusinessSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/business-settings"
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <AdminBusinessSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute>
              {runtimeBrand.isMarketplaceHost ? (
                <MarketplaceSuperAdminPage />
              ) : (
                <SuperAdminConsole />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminReportsHome />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/client-assignment"
          element={
            <ProtectedRoute>
              <SuperAdminClientAssignment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/billing-risk-alerts"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminBillingRiskAlerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/billing-coupons"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminBillingCoupons />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/registration-activity"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminRegistrationActivity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/login-activity"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminLoginActivity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/registration-analytics"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(<SuperAdminRegistrationAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/login-analytics"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(<SuperAdminLoginAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/billing-coupons"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(<SuperAdminBillingCouponReport />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/marketing-campaigns"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="marketing-campaigns" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/email-subscription"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="email-subscription" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/walk-in-queue"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="walk-in-queue" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/profitability-estimate"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="profitability-estimate" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/ticket-summary"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="ticket-summary" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/daily-closing"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="daily-closing" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/cash-balance"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="cash-balance" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/sales-tickets"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="sales-tickets" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/training-progress"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="training-progress" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/gift-cards-cash-drawer"
          element={
            <ProtectedRoute requireSuperAdmin>
              {withSuperAdminReportsShell(
                <SuperAdminExtendedReport reportKey="gift-cards-cash-drawer" />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/kpis"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminReports />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/sales-by-employee"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminSalesByEmployee />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/tax-liability"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminTaxLiabilitySummary />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/total-sheet"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminTotalSheet />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/appointments-status"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminAppointmentsStatus />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/cancellations"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminCancellationsNoShows />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/service-analytics"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminServiceAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/category-analytics"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminCategoryAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/client-analytics"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminClientAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/client-satisfaction"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminClientSatisfaction />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/complaint-themes"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminComplaintThemes />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/staff-performance"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminStaffPerformance />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/live-dashboard"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminLiveDashboard />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/gift-card-analytics"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminGiftCardAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/rewards"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminRewardsAnalytics />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/maintenance-cleaning"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminMaintenanceCleaningAnalyticsReport />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/salon-valuation"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminSalonValuationReport />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/break-even-sensitivity"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminBreakEvenSensitivityReport />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/reports/bug-reports"
          element={
            <ProtectedRoute>
              {withSuperAdminReportsShell(<SuperAdminBugReports />)}
            </ProtectedRoute>
          }
        />
        </Routes>
      </AppShell>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
        <RentalsFeatureProvider>
          <BrowserRouter>
            <UiModalProvider>
              <AppContent />
            </UiModalProvider>
          </BrowserRouter>
        </RentalsFeatureProvider>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;

